import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  ListItemText,
  Paper,
  Popper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { stationsEnum } from '../helpers';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const currentStationMessage = defineMessage({
  id: 'logger.streamScreen.youtubeStats.currentStation',
  defaultMessage: 'Emisora actual',
});

const allStationsMessage = defineMessage({
  id: 'logger.streamScreen.youtubeStats.allStations',
  defaultMessage: 'Todos los canales',
});

const selectStationsMessage = defineMessage({
  id: 'logger.streamScreen.youtubeStats.selectStations',
  defaultMessage: 'Seleccionar canales',
});

const selectPlaceholderMessage = defineMessage({
  id: 'common.select',
  defaultMessage: 'Seleccionar',
});

const clearMessage = defineMessage({
  id: 'common.text.clear',
  defaultMessage: 'Borrar',
});

const applyMessage = defineMessage({
  id: 'common.text.filter',
  defaultMessage: 'Aplicar',
});

const oneHourMessage = defineMessage({
  id: 'logger.streamScreen.youtubeStats.hour',
  defaultMessage: 'Hora',
});

const hoursMessage = defineMessage({
  id: 'logger.streamScreen.youtubeStats.hours',
  defaultMessage: 'Horas',
});

export type YoutubeStatsToolbarV2Props = {
  stationsSelection: string;
  setStationsSelection: (stationsSelection: string) => void;
  stationsList: { channelId: string; channelName: string }[];
  selectedStations: string[];
  setSelectedStations: (selectedStations: string[]) => void;
  dateRange: { label: string; range: { from: string; to: string } };
  dateRanges: { label: string; range: { from: string; to: string } }[];
  handleDateRangeChange: (selectedLabel: string) => void;
};

/**
 * Scope toggles (current / all / multi-select), optional channel picker, and time window toggles for YouTube stats v2.
 */
export function YoutubeStatsToolbarV2(props: YoutubeStatsToolbarV2Props) {
  const {
    stationsSelection,
    setStationsSelection,
    stationsList,
    selectedStations,
    setSelectedStations,
    dateRange,
    dateRanges,
    handleDateRangeChange,
  } = props;

  const [selectValue, setSelectValue] = useState(selectedStations);
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setSelectValue(selectedStations);
  }, [selectedStations]);

  return (
    <Box
      sx={{
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: 2,
        mx: 4,
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small"
          value={stationsSelection}
          exclusive
          onChange={(_, value) => value && setStationsSelection(value)}
        >
          <ToggleButton value={stationsEnum.current}>
            <FormattedMessage {...currentStationMessage} />
          </ToggleButton>
          <ToggleButton value={stationsEnum.all}>
            <FormattedMessage {...allStationsMessage} />
          </ToggleButton>
          <ToggleButton value={stationsEnum.selection}>
            <FormattedMessage {...selectStationsMessage} />
          </ToggleButton>
        </ToggleButtonGroup>

        {stationsSelection === stationsEnum.selection && (
          <Box sx={{ width: 300, minWidth: 200 }}>
            <Button
              ref={anchorRef}
              size="small"
              variant="outlined"
              onClick={() => setIsPopperOpen(!isPopperOpen)}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                position: 'relative',
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'block',
                  width: 'calc(100% - 20px)',
                  typography: 'body1',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {selectValue.length === 0 ? (
                  <Box component="em" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage {...selectPlaceholderMessage} />
                  </Box>
                ) : (
                  selectValue
                    .map(
                      (id: string) =>
                        stationsList.find((station) => station.channelId === id)?.channelName,
                    )
                    .join(', ')
                )}
              </Box>
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  right: 5,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: 'text.secondary',
                }}
              >
                <ArrowDropDownIcon />
              </Box>
            </Button>

            <Popper
              open={isPopperOpen}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              sx={{ width: 300, zIndex: 1300 }}
            >
              <ClickAwayListener onClickAway={() => setIsPopperOpen(false)}>
                <Paper
                  sx={{
                    maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
                    width: '100%',
                    overflow: 'auto',
                  }}
                >
                  {stationsList.map((station) => (
                    <Box
                      key={station.channelId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        const newValue = selectValue.includes(station.channelId)
                          ? selectValue.filter((id) => id !== station.channelId)
                          : [...selectValue, station.channelId];
                        setSelectValue(newValue);
                      }}
                    >
                      <Checkbox checked={selectValue.includes(station.channelId)} />
                      <ListItemText primary={station.channelName} />
                    </Box>
                  ))}

                  <Paper
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1,
                      p: 1,
                      display: 'flex',
                      justifyContent: 'space-around',
                      bgcolor: 'background.paper',
                      boxShadow: (theme) => theme.shadows[2],
                    }}
                  >
                    <Button size="small" onClick={() => setSelectValue([])}>
                      <FormattedMessage {...clearMessage} />
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedStations(selectValue);
                        setIsPopperOpen(false);
                      }}
                    >
                      <FormattedMessage {...applyMessage} />
                    </Button>
                  </Paper>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>
        )}
      </Box>

      <ToggleButtonGroup
        size="small"
        value={dateRange.label}
        exclusive
        onChange={(_, value) => value && handleDateRangeChange(value)}
      >
        <ToggleButton value={dateRanges[0].label}>
          1 <FormattedMessage {...oneHourMessage} />
        </ToggleButton>
        <ToggleButton value={dateRanges[1].label}>
          3 <FormattedMessage {...hoursMessage} />
        </ToggleButton>
        <ToggleButton value={dateRanges[2].label}>
          6 <FormattedMessage {...hoursMessage} />
        </ToggleButton>
        <ToggleButton value={dateRanges[3].label}>
          12 <FormattedMessage {...hoursMessage} />
        </ToggleButton>
        <ToggleButton value={dateRanges[4].label}>
          24 <FormattedMessage {...hoursMessage} />
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}
